import { getChatbotResponse } from 'api/chat/_requests';
import { FileAttachment } from 'components/common/AttachmentPreview';
import { supportChat, Message as MessageType, Conversation } from 'data/chat';
import moment from 'moment';
import {
  createContext,
  PropsWithChildren,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
  useCallback
} from 'react';

interface ChatWidgetProps {
  isOpenChat: boolean;
  conversation: Conversation;
  setConversation: Dispatch<SetStateAction<Conversation>>;
  setIsOpenChat: Dispatch<SetStateAction<boolean>>;
  sentMessage: ({
    message,
    attachments
  }: {
    message?: string;
    attachments?: { images?: string[]; file?: FileAttachment };
  }) => void;
}

export const ChatWidgetContext = createContext({} as ChatWidgetProps);

const ChatWidgetProvider = ({ children }: PropsWithChildren) => {
  const [isOpenChat, setIsOpenChat] = useState(false);
  const [conversation, setConversation] = useState(supportChat);

  const sentMessage = useCallback(
    async ({
      message,
      attachments
    }: {
      message?: string;
      attachments?: { images?: string[]; file?: FileAttachment };
    }) => {
      const newMessages = [
        ...conversation.messages,
        {
          id: Date.now(),
          type: 'sent',
          time: moment().fromNow(),
          readAt: null,
          message,
          attachments
        } as MessageType
      ];
      const newConversation = { ...conversation, messages: newMessages };
      setConversation(newConversation);
      const response = await getChatbotResponse(message as string);
      const updatedMessages = [
        ...newConversation.messages,
        {
          id: Date.now(),
          type: 'received',
          time: moment().fromNow(),
          readAt: null,
          message: response?.response
        } as MessageType
      ];
      const updatedConversation = {
        ...newConversation,
        messages: updatedMessages
      };
      setConversation(updatedConversation);
    },
    [conversation]
  );

  return (
    <ChatWidgetContext.Provider
      value={{
        conversation,
        setConversation,
        isOpenChat,
        setIsOpenChat,
        sentMessage
      }}
    >
      {children}
    </ChatWidgetContext.Provider>
  );
};

export const useChatWidgetContext = () => useContext(ChatWidgetContext);

export default ChatWidgetProvider;
